import React from "react";
import { motion } from "framer-motion";
import {
  Crown, Monitor, EyeOff, AlertTriangle, Pencil, Trash2, Archive, User as UserIcon,
} from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { Avatar } from "../../components/primitives";
import { IconBtn } from "../../components/RowControls";
import { Toggle } from "../../components/Toggle";
import {
  memberName, displayMemberEmail, roleLabel, resolveReportingManagers, formatManagerName,
  permissionCount, permissionFields, normalizeReportingManagerIds,
} from "./teamHelpers";
import { hasDirectReports, isOrphan, memberId } from "./teamInsights";

// The card view of a member. Same actions as the table row — this is a second
// presentation of one dataset, not a second feature, so nothing here is
// reachable only from cards.

const statusTone = (s) => (s === "pending" ? "warning" : s === "active" ? "positive" : "neutral");

function Pill({ tone = "neutral", children, title }) {
  const t = useDashTheme();
  const map = {
    positive: [t.status.positiveSoft, t.status.positive],
    warning: [t.status.warningSoft, t.status.warning],
    negative: [t.status.negativeSoft, t.status.negative],
    info: [t.status.infoSoft, t.status.info],
    neutral: [t.status.neutralSoft, t.c.textTertiary],
    ai: [t.c.aiSoft, t.c.ai],
  };
  const [bg, fg] = map[tone] || map.neutral;
  return (
    <span
      title={title}
      style={{
        height: 22, padding: "0 8px", borderRadius: t.r.pill, display: "inline-flex", alignItems: "center",
        gap: 5, fontSize: t.font.size.xs, fontWeight: t.font.weight.medium, background: bg, color: fg,
        whiteSpace: "nowrap", maxWidth: "100%",
      }}
    >{children}</span>
  );
}

// Permissions as a ring rather than a number: at a glance you see how much of
// the org's permission surface this person holds.
function AccessRing({ granted, total, onClick, title }) {
  const t = useDashTheme();
  const r = 9;
  const c = 2 * Math.PI * r;
  const len = total ? (granted / total) * c : 0;
  const color = total && granted >= total - 1 ? t.status.positive : granted > 0 ? t.c.primary : t.c.textTertiary;
  return (
    <button
      type="button"
      title={title}
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0,
        background: "transparent", border: "none", padding: 0, cursor: onClick ? "pointer" : "default",
        fontFamily: t.font.family,
      }}
    >
      <svg width="22" height="22" viewBox="0 0 22 22" style={{ transform: "rotate(-90deg)" }} aria-hidden="true">
        <circle cx="11" cy="11" r={r} fill="none" stroke={t.c.surfaceHover} strokeWidth="3" />
        <circle cx="11" cy="11" r={r} fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeDasharray={`${len} ${c - len}`} />
      </svg>
      <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>
        {granted}/{total}
      </span>
    </button>
  );
}

function MemberCard({
  u, index, t, roles, allUsers, showCrm, isAdmin, currentUserId, insightContext, accent,
  onInfo, onEdit, onDelete, onArchive, onStealth, onPermissions,
}) {
  const isSelf = (u.id && u.id === currentUserId) || (u._id && u._id === currentUserId);
  const managers = resolveReportingManagers(u, allUsers);
  const orphan = isOrphan(u, insightContext);
  const root = !managers.length && hasDirectReports(u, insightContext);
  // Managers arrive as ids, partial refs or full objects, and there's a legacy
  // singular field alongside the array — normalize before comparing.
  const directs = root
    ? allUsers.filter((x) => normalizeReportingManagerIds(x).includes(memberId(u))).length
    : 0;
  const status = u.status || "active";
  const permTotal = permissionFields(showCrm).length;
  const permGranted = permissionCount(u, showCrm);
  const activated = u.isActivated;

  return (
    <motion.article
      className="tg-mcard"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, delay: Math.min(index, 11) * 0.025, ease: t.motion.ease.out }}
      whileHover={{ y: -2 }}
      onClick={() => onInfo(u)}
      // The card is one clickable region; without this its accessible name is
      // the avatar initials run into everything else on it.
      aria-label={memberName(u)}
      style={{
        position: "relative", background: t.c.surface, border: `1px solid ${t.c.border}`,
        borderRadius: t.r.lg, boxShadow: t.shadow.sm, overflow: "hidden", cursor: "pointer",
        display: "flex", flexDirection: "column", // keeps every footer on one baseline
      }}
    >
      {/* Identity stripe — the member's own deterministic accent, the same one
          their avatar uses, so a face and its card always agree. */}
      <span style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: accent }} />

      <div style={{ display: "flex", alignItems: "flex-start", gap: t.s[3], padding: `${t.s[4]}px ${t.s[3] + 2}px ${t.s[3]}px` }}>
        <span style={{ position: "relative", flexShrink: 0, display: "inline-flex" }}>
          <Avatar user={u} name={memberName(u)} size={44} />
          {activated != null && (
            <span
              title={activated ? "Activated" : "Not activated"}
              style={{
                position: "absolute", right: -1, bottom: -1, width: 12, height: 12, borderRadius: 99,
                background: activated ? t.status.positive : t.c.textTertiary,
                border: `2px solid ${t.c.surface}`,
              }}
            />
          )}
        </span>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
            <span style={{
              fontSize: t.font.size.base, fontWeight: t.font.weight.medium, color: t.c.text,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{memberName(u)}</span>
            {isSelf && <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, flexShrink: 0 }}>(you)</span>}
            {u.role === "org_admin" && <Crown size={13} style={{ color: t.status.warning, flexShrink: 0 }} />}
          </div>
          <div
            title={u.position || displayMemberEmail(u)}
            style={{
              fontSize: t.font.size.sm, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              color: u.position ? t.c.textSecondary : t.status.warning,
            }}
          >{u.position || "Position not set"}</div>
          <div
            title={displayMemberEmail(u)}
            style={{
              fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: 3,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}
          >{u.department || displayMemberEmail(u)}</div>
        </div>

        <div
          className="tg-mcard-actions"
          onClick={(e) => e.stopPropagation()}
          style={{ display: "inline-flex", alignItems: "center", gap: 1, flexShrink: 0 }}
        >
          <IconBtn title="View profile" onClick={() => onInfo(u)}><UserIcon size={15} /></IconBtn>
          <IconBtn title="Edit" onClick={() => onEdit(u)}><Pencil size={15} /></IconBtn>
          {/* Archiving yourself would lock you out of your own org. */}
          {isAdmin && !isSelf && status !== "archived" && (
            <IconBtn title="Archive" onClick={() => onArchive(u)}><Archive size={15} /></IconBtn>
          )}
          {!isSelf && <IconBtn title="Remove" danger onClick={() => onDelete(u)}><Trash2 size={15} /></IconBtn>}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", padding: `0 ${t.s[3] + 2}px ${t.s[3]}px` }}>
        <Pill tone={u.role === "org_admin" ? "warning" : "info"}>{roleLabel(roles, u.role)}</Pill>
        <Pill tone={statusTone(status)}>{status}</Pill>
        {u.hasDesktopAgent
          ? <Pill tone="positive"><Monitor size={11} /> Agent</Pill>
          : (u.latestAppVersion
            ? <Pill tone="neutral" title="Last seen app version">v{u.latestAppVersion}</Pill>
            : <Pill tone="neutral">No agent</Pill>)}
        {u.stealth_mode && <Pill tone="ai"><EyeOff size={11} /> Background</Pill>}
      </div>

      <div style={{
        display: "flex", alignItems: "center", gap: 10, marginTop: "auto",
        padding: `${t.s[2] + 2}px ${t.s[3] + 2}px`, borderTop: `1px solid ${t.c.border}`,
        background: t.c.surfaceMuted,
      }}>
        <span style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0, flex: 1 }}>
          {managers.length > 0 ? (
            <>
              <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, whiteSpace: "nowrap" }}>Reports to</span>
              <Avatar user={managers[0]} name={formatManagerName(managers[0])} size={20} />
              <span
                title={managers.map(formatManagerName).join(", ")}
                style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {formatManagerName(managers[0])}{managers.length > 1 ? ` +${managers.length - 1}` : ""}
              </span>
            </>
          ) : root ? (
            // No manager but people report to them — that's the top of the org,
            // not a gap. Flagging the founder as a data problem is noise.
            <>
              <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, whiteSpace: "nowrap" }}>Top of org</span>
              <span style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, whiteSpace: "nowrap" }}>
                {directs} direct{directs === 1 ? "" : "s"}
              </span>
            </>
          ) : (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: t.font.size.sm, color: orphan ? t.status.warning : t.c.textTertiary }}>
              {orphan && <AlertTriangle size={12} style={{ flexShrink: 0 }} />} No manager
            </span>
          )}
        </span>

        <AccessRing
          granted={permGranted}
          total={permTotal}
          title={`Permissions and reporting for ${memberName(u)}`}
          onClick={() => onPermissions(u)}
        />

        <span onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex", flexShrink: 0 }}>
          <Toggle
            on={!!u.stealth_mode}
            label={`Background mode for ${memberName(u)}`}
            onClick={(e) => { e.stopPropagation(); onStealth(u); }}
          />
        </span>
      </div>
    </motion.article>
  );
}

// Card skeleton that matches the real card's height, so switching views or
// paging doesn't bounce the page.
function CardSkeleton() {
  const t = useDashTheme();
  const bar = (w, h = 10) => (
    <span style={{ display: "block", width: w, height: h, borderRadius: 4, background: t.c.surfaceHover }} />
  );
  return (
    <div style={{
      background: t.c.surface, border: `1px solid ${t.c.border}`, borderRadius: t.r.lg,
      boxShadow: t.shadow.sm, overflow: "hidden", opacity: 0.7,
    }}>
      <div style={{ display: "flex", gap: t.s[3], padding: `${t.s[4]}px ${t.s[3] + 2}px ${t.s[3]}px` }}>
        <span style={{ width: 44, height: 44, borderRadius: 99, background: t.c.surfaceHover, flexShrink: 0 }} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7, paddingTop: 3 }}>
          {bar("60%", 12)}{bar("40%")}{bar("30%", 9)}
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, padding: `0 ${t.s[3] + 2}px ${t.s[3]}px` }}>
        {bar(56, 22)}{bar(52, 22)}{bar(48, 22)}
      </div>
      <div style={{ padding: `${t.s[2] + 2}px ${t.s[3] + 2}px`, borderTop: `1px solid ${t.c.border}`, background: t.c.surfaceMuted }}>
        {bar("55%", 20)}
      </div>
    </div>
  );
}

export function MemberCardGrid({
  members, loading, skeletonCount = 8, t, roles, allUsers, showCrm, isAdmin, currentUserId,
  insightContext, emptyState,
  onInfo, onEdit, onDelete, onArchive, onStealth, onPermissions,
}) {
  if (loading) {
    return (
      <div className="tg-team-grid">
        {Array.from({ length: skeletonCount }, (_, i) => <CardSkeleton key={i} />)}
      </div>
    );
  }
  if (!members.length) return emptyState;

  return (
    <div className="tg-team-grid">
      {members.map((u, i) => (
        <MemberCard
          key={u.id || u._id}
          u={u}
          index={i}
          t={t}
          roles={roles}
          allUsers={allUsers}
          showCrm={showCrm}
          isAdmin={isAdmin}
          currentUserId={currentUserId}
          insightContext={insightContext}
          accent={t.accentFor(memberName(u)).bg}
          onInfo={onInfo}
          onEdit={onEdit}
          onDelete={onDelete}
          onArchive={onArchive}
          onStealth={onStealth}
          onPermissions={onPermissions}
        />
      ))}
    </div>
  );
}
